import { createFileRoute } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { Badge, type BadgeProps } from '@/components/ui/Badge';
import { Empty } from '@/components/ui/Empty';
import {
  useIntegrationConnections,
  useWebhookEndpoints,
  useWebhookDeliveries,
  type IntegrationConnectionRow,
  type WebhookEndpointRow,
  type WebhookDeliveryRow,
} from '@/data/integrations';

// Integrations scaffold screen (Phase 7).
//
// SCAFFOLD ONLY. Connections and webhooks are modeled in the database, but no
// provider behaviour is active: Zapier/QuickBooks sync and webhook dispatch stay
// off until the provider is connected and the dispatch worker is enabled. The
// screen LEADS with a prominent amber banner saying exactly that, then renders
// read-only tables over the (pre-launch, empty) tables. There are no actions
// here — the data layer exposes reads only.
//
// Built on the same layout primitives as the owner BI dashboard / staff console
// (Section / SimpleTable / Num) so it reads as one product. Those helpers are
// copied per-route in this codebase (not shared), matching admin.bi.index.tsx /
// admin.staff.index.tsx.
//
// The /admin parent route already gates on the `admin` role server-side, so this
// child route inherits that guard (matches every other admin.* route).
export const Route = createFileRoute('/admin/integrations/')({
  component: IntegrationsScaffold,
});

type BadgeTone = NonNullable<BadgeProps['tone']>;

// ---------------------------------------------------------------------------
// Status tones
// ---------------------------------------------------------------------------

// integration_connections.status in disconnected/connected/error.
function connectionTone(status: string): BadgeTone {
  if (status === 'connected') return 'success';
  if (status === 'error') return 'danger';
  return 'neutral'; // disconnected / anything else
}

// webhook_deliveries.status in pending/delivered/failed.
function deliveryTone(status: string): BadgeTone {
  if (status === 'delivered') return 'success';
  if (status === 'failed') return 'danger';
  return 'warn'; // pending / anything else
}

// ---------------------------------------------------------------------------
// Layout primitives (mirror admin.bi.index.tsx / admin.staff.index.tsx)
// ---------------------------------------------------------------------------

function Section({
  id,
  title,
  description,
  action,
  children,
}: {
  id: string;
  title: string;
  description: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section aria-labelledby={`${id}-heading`} className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id={`${id}-heading`} className="text-[var(--text-xl)]">
            {title}
          </h2>
          <p className="mt-1 max-w-3xl text-[var(--color-ink-muted)] text-[var(--text-sm)]">
            {description}
          </p>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function SimpleTable({ columns, rows }: { columns: string[]; rows: ReactNode[][] }) {
  return (
    <div className="surface overflow-x-auto">
      <table className="min-w-full text-left text-[var(--text-sm)]">
        <thead>
          <tr className="border-b border-[var(--color-border)]">
            {columns.map((c) => (
              <th key={c} className="px-4 py-3 font-medium">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-[var(--color-border)] last:border-0">
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-3 align-top">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Num({ value }: { value: number }) {
  return <span className="font-medium tabular-nums">{value}</span>;
}

// ---------------------------------------------------------------------------
// Scaffold banner — leads the screen. Amber/warn tone, visually distinct from
// the read-only tables below, so it is unmistakable that no provider behaviour
// is live.
// ---------------------------------------------------------------------------

function ScaffoldBanner() {
  return (
    <section
      aria-labelledby="scaffold-banner-heading"
      className="surface-elevated space-y-3 border-l-4 border-[var(--color-warn)] p-6"
    >
      <div className="flex flex-wrap items-center gap-3">
        <Badge tone="warn">Scaffold</Badge>
        <h2
          id="scaffold-banner-heading"
          className="tracking-[var(--tracking-tight)] text-[var(--text-lg)]"
        >
          Provider configuration required
        </h2>
      </div>
      <p className="max-w-3xl leading-[var(--leading-relaxed)] text-[var(--color-ink-muted)]">
        Connections and webhooks are modeled here, but Zapier/QuickBooks sync and webhook dispatch
        stay inactive until the provider is connected and the dispatch worker is enabled.
      </p>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Connections
// ---------------------------------------------------------------------------

function ConnectionsSection({
  rows,
  loading,
  error,
}: {
  rows: IntegrationConnectionRow[];
  loading: boolean;
  error: boolean;
}) {
  return (
    <Section
      id="connections"
      title="Connections"
      description="Per-provider connection state (Zapier, QuickBooks, Google Calendar, webhook, export API). Read-only — providers are not connected until configured."
    >
      {loading ? (
        <p aria-live="polite">Loading…</p>
      ) : error ? (
        <Empty
          title="Connections unavailable"
          description="The integration connections could not be loaded for this session. Refresh to retry."
        />
      ) : rows.length === 0 ? (
        <Empty
          title="No connections yet"
          description="No providers have been configured for this studio. Connections will appear here once a provider is set up."
        />
      ) : (
        <SimpleTable
          columns={['Provider', 'Status']}
          rows={rows.map((c) => [
            <span className="font-medium capitalize">{c.provider.replaceAll('_', ' ')}</span>,
            <Badge tone={connectionTone(c.status)}>{c.status}</Badge>,
          ])}
        />
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Webhook endpoints
// ---------------------------------------------------------------------------

function EndpointsSection({
  rows,
  loading,
  error,
}: {
  rows: WebhookEndpointRow[];
  loading: boolean;
  error: boolean;
}) {
  return (
    <Section
      id="webhook-endpoints"
      title="Webhook endpoints"
      description="Registered endpoints and the events each subscribes to. Read-only — no events are dispatched until the dispatch worker is enabled."
    >
      {loading ? (
        <p aria-live="polite">Loading…</p>
      ) : error ? (
        <Empty
          title="Webhook endpoints unavailable"
          description="The webhook endpoints could not be loaded for this session. Refresh to retry."
        />
      ) : rows.length === 0 ? (
        <Empty
          title="No webhook endpoints yet"
          description="No endpoints have been registered. They will appear here once webhooks are configured for this studio."
        />
      ) : (
        <SimpleTable
          columns={['URL', 'Events', 'Active']}
          rows={rows.map((e) => [
            <span className="font-mono break-all text-[var(--text-xs)]">{e.url}</span>,
            e.events.length > 0 ? (
              <span className="flex flex-wrap gap-1.5">
                {e.events.map((ev) => (
                  <Badge key={ev} tone="neutral">
                    {ev}
                  </Badge>
                ))}
              </span>
            ) : (
              <span className="text-[var(--color-ink-muted)]">—</span>
            ),
            e.active ? <Badge tone="success">active</Badge> : <Badge tone="neutral">paused</Badge>,
          ])}
        />
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Recent deliveries
// ---------------------------------------------------------------------------

function DeliveriesSection({
  rows,
  loading,
  error,
}: {
  rows: WebhookDeliveryRow[];
  loading: boolean;
  error: boolean;
}) {
  return (
    <Section
      id="deliveries"
      title="Recent deliveries"
      description="The latest webhook delivery attempts and their outcomes. Read-only — populated by the dispatch worker once it is enabled."
    >
      {loading ? (
        <p aria-live="polite">Loading…</p>
      ) : error ? (
        <Empty
          title="Deliveries unavailable"
          description="The recent deliveries could not be loaded for this session. Refresh to retry."
        />
      ) : rows.length === 0 ? (
        <Empty
          title="No deliveries yet"
          description="No webhook deliveries have been attempted. Attempts will appear here once the dispatch worker is enabled and events fire."
        />
      ) : (
        <SimpleTable
          columns={['Event', 'Status', 'Attempts']}
          rows={rows.map((d) => [
            <span className="font-mono text-[var(--text-xs)]">{d.event}</span>,
            <Badge tone={deliveryTone(d.status)}>{d.status}</Badge>,
            <Num value={d.attempts} />,
          ])}
        />
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

function IntegrationsScaffold() {
  const connections = useIntegrationConnections();
  const endpoints = useWebhookEndpoints();
  const deliveries = useWebhookDeliveries();

  return (
    <div className="space-y-10">
      <header>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Platform
        </p>
        <h1 className="mt-2">Integrations</h1>
      </header>

      <ScaffoldBanner />

      <ConnectionsSection
        rows={connections.data ?? []}
        loading={connections.isLoading}
        error={Boolean(connections.error)}
      />
      <EndpointsSection
        rows={endpoints.data ?? []}
        loading={endpoints.isLoading}
        error={Boolean(endpoints.error)}
      />
      <DeliveriesSection
        rows={deliveries.data ?? []}
        loading={deliveries.isLoading}
        error={Boolean(deliveries.error)}
      />
    </div>
  );
}
